// Pure helpers for the domain Manage panel (components/DomainManagePanel.jsx).
//
// The panel runs the customer assistant's own domain tools through
// POST /api/accounts/:id/domain-actions. Those tools answer in two layers:
//   envelope  { ok, result, error, errorCode }   — did the tool run at all
//   result    { preview, message, ... }          — what the tool said
// and a tool that refuses its own input answers ok:true with
// result.ok === false. readOutcome folds the three shapes into one.

export const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV'];

/**
 * @returns {{ ok: boolean, preview: boolean, message: string, result: any,
 *            nameserverChangeRequired: boolean, errorCode: string|null }}
 */
export function readOutcome(envelope) {
  const env = envelope || {};
  if (env.ok === false) {
    return {
      ok: false, preview: false, result: null, nameserverChangeRequired: false,
      message: env.error || 'The action failed.',
      errorCode: env.errorCode || null,
    };
  }
  const result = env.result ?? null;
  if (result && result.ok === false) {
    return {
      ok: false, preview: false, result, nameserverChangeRequired: false,
      message: result.error || result.message || 'The action was refused.',
      errorCode: result.errorCode || null,
    };
  }
  return {
    ok: true,
    preview: result?.preview === true,
    message: typeof result?.message === 'string' ? result.message : '',
    result,
    nameserverChangeRequired: result?.nameserverChangeRequired === true,
    errorCode: null,
  };
}

const HOSTNAME = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * Nameservers typed one per line, or comma / space separated.
 * @returns {{ nameservers: string[], error: string }}
 */
export function parseNameservers(text) {
  const seen = new Set();
  const nameservers = [];
  for (const raw of String(text || '').split(/[\s,]+/)) {
    const ns = raw.trim().toLowerCase().replace(/\.$/, '');
    if (!ns || seen.has(ns)) continue;
    seen.add(ns);
    nameservers.push(ns);
  }
  const bad = nameservers.filter(ns => !HOSTNAME.test(ns));
  if (bad.length) return { nameservers, error: `Not a hostname: ${bad.join(', ')}` };
  if (nameservers.length < 2) return { nameservers, error: 'Enter at least two nameservers.' };
  if (nameservers.length > 13) return { nameservers, error: 'At most 13 nameservers.' };
  return { nameservers, error: '' };
}

/**
 * Validate a DNS record form before it reaches the tool, so the common typos
 * get a field-level message instead of a registrar round trip.
 * @returns {string} '' when valid, otherwise the message
 */
export function validateRecord({ type, value, ttl } = {}) {
  if (!DNS_TYPES.includes(String(type || '').toUpperCase())) return 'Pick a record type.';
  const v = String(value || '').trim();
  if (!v) return 'Value is required.';
  const t = String(type).toUpperCase();
  if (t === 'A' && !/^(\d{1,3}\.){3}\d{1,3}$/.test(v)) return 'An A record needs an IPv4 address.';
  if (t === 'AAAA' && !v.includes(':')) return 'An AAAA record needs an IPv6 address.';
  if (ttl !== undefined && ttl !== null && ttl !== '') {
    const n = Number(ttl);
    if (!Number.isInteger(n) || n < 60 || n > 86400) return 'TTL must be a whole number between 60 and 86400.';
  }
  return '';
}

/** Forwarding targets must carry their scheme; the tool refuses otherwise. */
export function validateForwardTarget(target) {
  const t = String(target || '').trim();
  if (!/^https?:\/\/[^\s/]+\.[^\s]+/i.test(t)) return 'Enter a full URL starting with http:// or https://.';
  return '';
}
